"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { X } from "lucide-react";
import { PitchPlayer } from "./pitch-player";
import { PITCH_GENERAL } from "./pitch-data";

/*
 * The 3-minute pitch as a one-time pop-up: the first page a visitor opens on askfinz.com, whatever
 * it is, offers it once. Seen is remembered in a 400-day cookie plus localStorage, set the moment it
 * opens, so a reload or a second tab never shows it again, and a closed one never nags.
 *
 * It stays out of the way where it would be wrong: on pages that already carry the video
 * (/colab/easi, and the homepage's own #pitch link), for automated traffic (the same bot test the
 * analytics loader uses), and inside embeds. It plays with sound: when the browser refuses that
 * without a click, the player shows "Play with sound" rather than starting silent.
 *
 * It cannot be dismissed for the first 30 seconds: the close button sits greyed out with a
 * countdown, and Escape and a click outside do nothing until it unlocks.
 */

const KEY = "askfinz_pitch_popup_v3";
const LOCK_S = 30;
const SKIP = [/^\/colab\//, /^\/collab\//, /^\/embed/, /^\/og/];
const BOT = /bot|crawl|spider|slurp|headless|puppeteer|playwright|lighthouse|pagespeed|gtmetrix|pingdom|uptime|monitor|askfinz/i;

// "Seen" lives in two places, and either one is enough: a first-party cookie that lasts 400 days
// (the most Chrome allows) and is shared across askfinz.com and its subdomains, and localStorage as
// a fallback where cookies are cleared on exit. A visitor who comes back months later does not get
// the video again. The cookie is a flag, not a tracker: no id, never sent anywhere that reads it.
const COOKIE = "askfinz_pitch_seen";
const MAX_AGE = 60 * 60 * 24 * 400;
function seen(): boolean {
  if (document.cookie.split("; ").some((c) => c.startsWith(`${COOKIE}=`))) return true;
  try { return !!localStorage.getItem(KEY); } catch { return false; }
}
function markSeen() {
  const host = window.location.hostname;
  const domain = host === "askfinz.com" || host.endsWith(".askfinz.com") ? "; domain=askfinz.com" : "";
  document.cookie = `${COOKIE}=1; max-age=${MAX_AGE}; path=/; samesite=lax${window.location.protocol === "https:" ? "; secure" : ""}${domain}`;
  try { localStorage.setItem(KEY, new Date().toISOString()); } catch { /* the cookie alone remembers it */ }
}

export function PitchPopup() {
  const pathname = usePathname() || "/";
  const [open, setOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [left, setLeft] = useState(LOCK_S);
  const canClose = left <= 0;
  const close = () => { if (canClose) setOpen(false); };

  // The unlock countdown, from the moment it opens.
  useEffect(() => {
    if (!open) return;
    const t0 = Date.now();
    const iv = setInterval(() => {
      const l = Math.max(0, LOCK_S - Math.floor((Date.now() - t0) / 1000));
      setLeft(l);
      if (l <= 0) clearInterval(iv);
    }, 250);
    return () => clearInterval(iv);
  }, [open]);

  useEffect(() => {
    if (SKIP.some((r) => r.test(pathname))) return;
    if (typeof window === "undefined" || window.self !== window.top) return;
    const n = navigator;
    if (n.webdriver || BOT.test(n.userAgent || "")) return;
    if (/[?&]nopitch\b/.test(window.location.search) || window.location.hash === "#pitch") return;
    if (seen()) return;
    const id = setTimeout(() => {
      markSeen();
      setOpen(true);
    }, 1200);
    return () => clearTimeout(id);
    // Only the landing page decides; later client-side navigations never re-open it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open || !canClose) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, canClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pitch-popup-title"
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-8"
      style={{ animation: "pitchPopFade .3s ease-out" }}
    >
      <style>{`@keyframes pitchPopFade{from{opacity:0}to{opacity:1}}@keyframes pitchPopRise{from{opacity:0;transform:translateY(18px) scale(.97)}to{opacity:1;transform:none}}`}</style>
      <div aria-hidden onClick={close} className={`absolute inset-0 bg-black/75 backdrop-blur-sm ${canClose ? "cursor-pointer" : ""}`} />
      <div ref={boxRef} className="relative w-full max-w-5xl overflow-hidden rounded-3xl border border-white/10 bg-[#0e0d0c] text-white shadow-[0_40px_120px_rgba(0,0,0,.6)]" style={{ animation: "pitchPopRise .35s cubic-bezier(.2,.8,.2,1)" }}>
        <div className="flex items-center justify-between gap-4 px-5 py-4 md:px-6">
          <div className="min-w-0">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-[#ff5a50]">askFinz in 3 minutes</div>
            <h2 id="pitch-popup-title" className="mt-1 truncate text-[17px] font-semibold tracking-tight md:text-xl">
              Our own index of the web, and what we build on it.
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            disabled={!canClose}
            aria-label={canClose ? "Close the video" : `Close available in ${left} seconds`}
            title={canClose ? "Close" : `You can close this in ${left}s`}
            onClick={close}
            className={`relative grid h-10 w-10 shrink-0 place-items-center rounded-full transition ${canClose ? "bg-white/15 text-white hover:bg-white/25" : "cursor-not-allowed bg-white/5 text-white/35"}`}
          >
            {canClose ? (
              <X className="h-4 w-4" />
            ) : (
              <>
                <svg aria-hidden viewBox="0 0 40 40" className="absolute inset-0 h-10 w-10 -rotate-90">
                  <circle cx="20" cy="20" r="18" fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="2.5" />
                  <circle cx="20" cy="20" r="18" fill="none" stroke="rgba(255,255,255,.4)" strokeWidth="2.5" strokeLinecap="round"
                    strokeDasharray={113.1} strokeDashoffset={113.1 * (left / LOCK_S)} style={{ transition: "stroke-dashoffset .25s linear" }} />
                </svg>
                <span className="font-mono text-[12px] tabular-nums">{left}</span>
              </>
            )}
          </button>
        </div>
        <PitchPlayer {...PITCH_GENERAL} autoPlay loop className="!rounded-none" title="askFinz in 3 minutes" />
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-[12px] text-white/55 md:px-6">
          <span>Shown once. Replay it any time on the homepage.</span>
          {canClose ? (
            <a href="/#pitch" onClick={() => setOpen(false)} className="text-white/75 underline-offset-4 hover:text-white hover:underline">askfinz.com/#pitch</a>
          ) : (
            <span className="text-white/35">You can close this in {left}s</span>
          )}
        </div>
      </div>
    </div>
  );
}
